/**
 * ArchivesPage — récolement des archives communales (item 11.2, mig 275).
 *
 * ─── DEUX ÉCRANS EN UN, ET C'EST VOULU ──────────────────────────────────────
 *
 * L'INVENTAIRE se tient au bureau : les contenants, leur métrage, ce qu'ils
 * contiennent. Le RÉCOLEMENT se fait debout dans le grenier, avec un téléphone :
 * on passe devant chaque armoire, on constate, on dicte.
 *
 * Les deux vivent au même endroit parce que c'est la même matière, mais l'onglet
 * « Récolement » est pensé pour le terrain : une ligne par contenant, trois
 * boutons de constat, et la dictée pour les observations. Personne ne tape au
 * clavier debout dans un grenier.
 *
 * ─── LE MÉTRAGE LINÉAIRE ────────────────────────────────────────────────────
 *
 * C'est la mesure du métier, jamais un nombre de boîtes : une boîte n'a pas
 * toujours la même épaisseur. L'écart entre le métrage déclaré et le métrage
 * constaté est le premier signal qu'il manque quelque chose.
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Badge, Alert, Button, Input, Field, ModuleHeader } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { useDictee } from '@/hooks/useDictee';
import { PlanSimple, type ElementPlan, type TraitPlan } from '@/components/PlanSimple';
import { api, ApiException, type LieuDTO } from '@/lib/api';
import type {
  ArchivesContenant, ArchivesSerie, ArchivesUnite, ArchivesCategorie,
  ArchivesRecolement, ArchivesRecolementLigne,
} from '@roronoa-collectivites/shared-types';
import {
  Archive, Plus, Info, Trash2, ClipboardCheck, Mic, MicOff, Check, X, HelpCircle,
  QrCode, Upload, Download, Tags, ScanLine, Map, Boxes,
  Camera,
} from 'lucide-react';
import { ArchivesEliminationPanel, CategorieDuaEditor } from '@/components/archives/ArchivesEliminationPanel';
import { FantomeDossier } from '@/components/archives/FantomeDossier';

const fr = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

const TYPES_CONTENANT = [
  { v: 'armoire', l: 'Armoire' },
  { v: 'rayonnage', l: 'Rayonnage' },
  { v: 'travee', l: 'Travée' },
  { v: 'etagere', l: 'Étagère' },
  { v: 'coffre', l: 'Coffre' },
  { v: 'autre', l: 'Autre' },
] as const;

export function ArchivesPage() {
  const { confirm, prompt, dialogElement } = useConfirmDialog();

  const [params, setParams] = useSearchParams();
  const [onglet, setOnglet] = useState<
    'inventaire' | 'recolement' | 'categories' | 'plan' | 'eliminations'>('inventaire');
  // ── Le plan du local (mig 276) ──
  const [locaux, setLocaux] = useState<Array<{
    id: string; libelle: string; lieuNom: string | null;
    largeur: number; hauteur: number; nbContenants: number;
  }>>([]);
  const [localPlan, setLocalPlan] = useState<string>('');
  const [planElements, setPlanElements] = useState<ElementPlan[]>([]);
  const [planLocal, setPlanLocal] = useState<{
    libelle: string; largeur: number; hauteur: number;
    largeurM: number | null; hauteurM: number | null; totalMetrage: number;
  } | null>(null);
  const [planTraits, setPlanTraits] = useState<TraitPlan[]>([]);
  const [categories, setCategories] = useState<ArchivesCategorie[]>([]);
  const [importRapport, setImportRapport] = useState<{
    contenantsCrees: number; unitesCreees: number; categoriesCreees: number;
    ignorees: number; erreurs: string[];
  } | null>(null);

  /**
   * ─── LE SCAN — l'usage de tous les jours ────────────────────────────────
   *
   * `?scan=ARM-03` (armoire) ou `?scanCat=ETAT-CIVIL` (catégorie). On affiche
   * d'abord CE QU'IL Y A DEDANS : c'est le besoin premier de l'item 11.2, la
   * campagne de récolement n'est que le deuxième temps.
   */
  const [scan, setScan] = useState<
    | { type: 'contenant'; data: Awaited<ReturnType<typeof api.admin.archives.scanContenant>> }
    | { type: 'categorie'; data: Awaited<ReturnType<typeof api.admin.archives.scanCategorie>> }
    | null
  >(null);
  const [contenants, setContenants] = useState<ArchivesContenant[]>([]);
  const [series, setSeries] = useState<ArchivesSerie[]>([]);
  const [lieux, setLieux] = useState<LieuDTO[]>([]);
  const [unites, setUnites] = useState<Record<string, ArchivesUnite[]>>({});
  const [ouvert, setOuvert] = useState<string | null>(null);

  const [recolements, setRecolements] = useState<ArchivesRecolement[]>([]);
  const [campagne, setCampagne] = useState<string | null>(null);
  const [etat, setEtat] = useState<ArchivesRecolementLigne[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [form, setForm] = useState<{
    code: string; libelle: string; typeContenant: string;
    lieuId: string; local: string; metrage: string;
    largeurSol: string; profondeurSol: string;
  } | null>(null);

  // Le contenant en cours de constat, et son observation dictée.
  const [constatDe, setConstatDe] = useState<string | null>(null);
  const [observation, setObservation] = useState('');
  const dictee = useDictee('fr-FR', (t) => setObservation((o) => (o ? `${o} ${t}` : t)));

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [rc, rs, rr, rcat, rl] = await Promise.all([
        api.admin.archives.contenants(),
        api.admin.archives.series(),
        api.admin.archives.recolements(),
        api.admin.archives.categories(),
        api.admin.archives.locaux(),
      ]);
      setContenants(rc.contenants); setSeries(rs.series);
      setRecolements(rr.recolements); setCategories(rcat.categories);
      setLocaux(rl.locaux);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  useEffect(() => {
    void (async () => {
      try { setLieux((await api.admin.lieux.list()).lieux); } catch { /* liste seule */ }
    })();
  }, []);

  // Le QR scanné arrive par l'URL. On charge, on affiche, et on retire le
  // paramètre pour qu'un rafraîchissement ne rejoue pas indéfiniment le scan.
  useEffect(() => {
    const codeArmoire = params.get('scan');
    const codeCat = params.get('scanCat');
    if (!codeArmoire && !codeCat) return;
    void (async () => {
      try {
        if (codeArmoire) {
          setScan({ type: 'contenant', data: await api.admin.archives.scanContenant(codeArmoire) });
        } else if (codeCat) {
          setScan({ type: 'categorie', data: await api.admin.archives.scanCategorie(codeCat) });
        }
      } catch (e) {
        setError(e instanceof ApiException ? e.message : 'Code inconnu');
      } finally {
        const p = new URLSearchParams(params);
        p.delete('scan'); p.delete('scanCat');
        setParams(p, { replace: true });
      }
    })();
  }, [params, setParams]);

  const chargerPlan = useCallback(async (id: string) => {
    if (!id) { setPlanElements([]); setPlanLocal(null); return; }
    try {
      const r = await api.admin.archives.planLocal(id);
      setPlanLocal({ ...r.local, totalMetrage: r.totalMetrage });
      setPlanTraits(r.traits);
      setPlanElements(r.contenants.map((c) => ({
        id: c.id, code: c.code, libelle: c.libelle,
        x: c.planX, y: c.planY,
        badge: c.metrageLineaire !== null
          ? `${c.metrageOccupe.toFixed(1)} / ${c.metrageLineaire.toFixed(1)} m`
          : `${c.nbUnites} unité${c.nbUnites > 1 ? 's' : ''}`,
        // L'armoire qui déborde saute aux yeux — c'est ce qu'on cherche.
        alerte: c.metrageLineaire !== null && c.metrageOccupe > c.metrageLineaire,
        // L'encombrement AU SOL, jamais le métrage linéaire : celui-ci est la
        // capacité (cumul des tablettes), et dessiner un meuble avec donnerait
        // une armoire de six mètres de large.
        largeurReelleM: c.largeurSol,
        profondeurReelleM: c.profondeurSol,
      })));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Plan illisible');
    }
  }, []);

  async function deplacerContenant(id: string, x: number, y: number) {
    setPlanElements((ps) => ps.map((p) => (p.id === id ? { ...p, x, y } : p)));
    try {
      await api.admin.archives.positionnerContenant(id, {
        planX: x, planY: y, localId: localPlan || null,
      });
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Position non enregistrée');
      await chargerPlan(localPlan);
    }
  }

  async function creerLocal() {
    const libelle = await prompt({
      title: 'Nouveau local d’archives',
      description: 'Une mairie archive souvent à plusieurs endroits — grenier, sous-sol. Chacun a son plan.',
      placeholder: 'Ex. : Grenier de la mairie',
      required: true, maxLength: 160,
      confirmLabel: 'Créer',
    });
    if (!libelle) return;
    try {
      const r = await api.admin.archives.creerLocal({ libelle });
      await charger();
      setLocalPlan(r.id); await chargerPlan(r.id);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Création impossible');
    }
  }

  async function importer(fichier: File) {
    setError(null); setImportRapport(null);
    try {
      const r = await api.admin.archives.importer(fichier);
      setImportRapport(r);
      setMsg(`${r.unitesCreees} archives importées, ${r.contenantsCrees} contenants créés.`);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Import impossible');
    }
  }

  async function creerCategorie() {
    const libelle = await prompt({
      title: 'Nouvelle catégorie',
      description: 'C’est VOTRE nomenclature : le « type de données » que vous utilisez déjà. Rien n’est imposé.',
      placeholder: 'Ex. : État civil, Urbanisme, Comptabilité',
      required: true, maxLength: 200,
      confirmLabel: 'Créer',
    });
    if (!libelle) return;
    try {
      await api.admin.archives.creerCategorie({
        code: libelle.slice(0, 40), libelle, actif: true,
      });
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Création impossible');
    }
  }

  async function supprimerCategorie(c: ArchivesCategorie) {
    const ok = await confirm({
      title: 'Supprimer cette catégorie ?',
      description: `« ${c.libelle} » sera effacée. Ce n’est possible que si aucune archive ne la porte — sinon désactivez-la.`,
      danger: true, confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await api.admin.archives.supprimerCategorie(c.id);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  // M4 — photo du contenant (dépôt depuis le téléphone, consultation).
  const [photoEnvoi, setPhotoEnvoi] = useState<string | null>(null);
  async function deposerPhoto(id: string, f: File) {
    setPhotoEnvoi(id);
    try {
      await api.admin.archives.photoContenant.deposer(id, f);
      setMsg('Photo enregistrée — elle figurera sur le procès-verbal de récolement.');
      await charger();
    } catch (e) {
      setMsg(e instanceof ApiException ? e.payload.error : 'Envoi de la photo impossible');
    } finally { setPhotoEnvoi(null); }
  }
  async function voirPhoto(id: string) {
    try {
      const r = await api.admin.archives.photoContenant.lien(id);
      if (r.url) window.open(r.url, '_blank', 'noopener');
    } catch (e) {
      setMsg(e instanceof ApiException ? e.payload.error : 'Photo indisponible');
    }
  }

  // M5 — après une sortie ou un retour, relire l'armoire scannée.
  async function rescanner() {
    if (scan?.type !== 'contenant') return;
    try { setScan({ type: 'contenant', data: await api.admin.archives.scanContenant(scan.data.contenant.code) }); }
    catch { /* le scan reste affiché tel quel */ }
  }

  async function ouvrirUnites(id: string) {
    if (ouvert === id) { setOuvert(null); return; }
    setOuvert(id);
    try {
      const r = await api.admin.archives.unites(id);
      setUnites((u) => ({ ...u, [id]: r.unites }));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Contenu illisible');
    }
  }

  async function creerContenant() {
    if (!form?.code.trim() || !form.libelle.trim()) {
      setError('Le code et le libellé sont obligatoires'); return;
    }
    try {
      await api.admin.archives.creerContenant({
        code: form.code.trim(), libelle: form.libelle.trim(),
        typeContenant: form.typeContenant as 'armoire',
        lieuId: form.lieuId || null, local: form.local || null,
        metrageLineaire: form.metrage ? Number(form.metrage) : null,
        largeurSol: form.largeurSol ? Number(form.largeurSol) : null,
        profondeurSol: form.profondeurSol ? Number(form.profondeurSol) : null,
        actif: true,
      });
      setForm(null); await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Création impossible');
    }
  }

  async function supprimerContenant(c: ArchivesContenant) {
    const ok = await confirm({
      title: 'Supprimer ce contenant ?',
      description: `« ${c.code} — ${c.libelle} » sera effacé. Ce n’est possible que s’il est vide : la trace de ce qu’il contenait ne doit pas disparaître.`,
      danger: true, confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await api.admin.archives.supprimerContenant(c.id);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  async function ouvrirCampagne() {
    const libelle = await prompt({
      title: 'Ouvrir une campagne de récolement',
      description: 'Le procès-verbal de récolement est obligatoire à chaque changement de maire (code du patrimoine, art. L212-11).',
      placeholder: 'Ex. : Récolement 2026 — changement de maire',
      required: true, maxLength: 200,
      confirmLabel: 'Ouvrir',
    });
    if (!libelle) return;
    try {
      const r = await api.admin.archives.ouvrirRecolement({ libelle });
      setCampagne(r.id); setOnglet('recolement');
      await charger(); await chargerEtat(r.id);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Ouverture impossible');
    }
  }

  const chargerEtat = useCallback(async (id: string) => {
    try {
      const r = await api.admin.archives.etatRecolement(id);
      setEtat(r.etat);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'État illisible');
    }
  }, []);

  async function constater(
    contenantId: string, constat: 'conforme' | 'ecart' | 'introuvable',
  ) {
    if (!campagne) return;
    dictee.arreter();
    try {
      await api.admin.archives.enregistrerPassage(campagne, {
        contenantId, constat,
        observations: observation.trim() || null,
      });
      setConstatDe(null); setObservation('');
      await chargerEtat(campagne);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Constat non enregistré');
    }
  }

  async function clore() {
    if (!campagne) return;
    try {
      await api.admin.archives.cloreRecolement(campagne);
      setMsg('Campagne close. Le procès-verbal atteste de l’ensemble des contenants.');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Clôture impossible');
    }
  }

  const champ = 'h-10 w-full rounded-md border border-border bg-surface/60 px-2 text-sm text-ink';
  const campagneCourante = recolements.find((r) => r.id === campagne);

  return (
    <div className="min-h-screen text-ink">
      {dialogElement}
      <ModuleHeader accent="grc" icon={Archive} eyebrow="Paramètres" title="Archives communales" />

      <main id="main-content" className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6">
        {error && <Alert variant="error">{error}</Alert>}
        {msg && <Alert variant="success">{msg}</Alert>}

        <div className="flex gap-2">
          <Button variant={onglet === 'inventaire' ? 'primary' : 'glass'} size="sm"
            onClick={() => setOnglet('inventaire')}>Inventaire</Button>
          <Button variant={onglet === 'recolement' ? 'primary' : 'glass'} size="sm"
            onClick={() => setOnglet('recolement')}>Récolement</Button>
          <Button variant={onglet === 'categories' ? 'primary' : 'glass'} size="sm"
            onClick={() => setOnglet('categories')}>Catégories</Button>
          <Button variant={onglet === 'plan' ? 'primary' : 'glass'} size="sm"
            onClick={() => setOnglet('plan')}>Plan</Button>
          {/* M3 (retour Manon) — DUA échue → bordereau → visa des Archives départementales. */}
          <Button variant={onglet === 'eliminations' ? 'primary' : 'glass'} size="sm"
            onClick={() => setOnglet('eliminations')}>Éliminations</Button>
        </div>

        {/* ── Le résultat d'un scan, en tête : c'est pour ça qu'on est là ── */}
        {scan && (
          <Card variant="glass" padded>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <ScanLine className="h-4 w-4 text-grc" aria-hidden="true" />
                {scan.type === 'contenant'
                  ? `${scan.data.contenant.code} — ${scan.data.contenant.libelle}`
                  : `Catégorie ${scan.data.categorie.libelle}`}
              </h2>
              <Button size="sm" variant="ghost" onClick={() => setScan(null)}>Fermer</Button>
            </div>

            {scan.type === 'contenant' ? (
              <>
                {(scan.data.contenant.lieuNom || scan.data.contenant.local) && (
                  <p className="mb-2 text-2xs text-ink-subtle">
                    {[scan.data.contenant.lieuNom, scan.data.contenant.local]
                      .filter(Boolean).join(' · ')}
                  </p>
                )}
                {scan.data.unites.length === 0 ? (
                  <p className="text-sm text-ink-subtle">Ce contenant est vide.</p>
                ) : (
                  <ul className="space-y-0.5">
                    {scan.data.unites.map((u) => (
                      <li key={u.id} className="text-sm text-ink">
                        {u.numeroArchives && (
                          <span className="tabular-nums text-ink-muted">{u.numeroArchives} · </span>
                        )}
                        {u.intitule}
                        {u.categorieLibelle && (
                          <span className="ml-2 text-2xs text-ink-subtle">{u.categorieLibelle}</span>
                        )}
                        {(u.anneeDebut || u.anneeFin) && (
                          <span className="text-ink-muted"> ({u.anneeDebut ?? '?'}–{u.anneeFin ?? '?'})</span>
                        )}
                        {/* M5 — la fiche fantôme : qui a pris ce dossier, et quand. */}
                        <FantomeDossier unite={u} onChange={() => void rescanner()} />
                      </li>
                    ))}
                  </ul>
                )}
                {/* Le constat n'apparaît QUE s'il y a une campagne ouverte :
                    scanner pour consulter est le cas le plus fréquent. */}
                {scan.data.campagneEnCours && (
                  <Alert variant="info" className="mt-3">
                    Campagne « {scan.data.campagneEnCours.libelle} » en cours —
                    {scan.data.campagneEnCours.dejaVu
                      ? ` ce contenant a déjà été constaté (${scan.data.campagneEnCours.constat}).`
                      : ' ce contenant n’a pas encore été vu.'}{' '}
                    <button type="button" className="underline"
                      onClick={() => {
                        setCampagne(scan.data.campagneEnCours!.id);
                        void chargerEtat(scan.data.campagneEnCours!.id);
                        setOnglet('recolement'); setScan(null);
                      }}>
                      Aller au récolement
                    </button>
                  </Alert>
                )}
              </>
            ) : (
              <ul className="space-y-0.5">
                {scan.data.unites.map((u) => (
                  <li key={u.id} className="text-sm text-ink">
                    {u.intitule}
                    <span className="text-2xs text-ink-subtle">
                      {' '}— {u.contenantCode}
                      {u.lieuNom ? ` · ${u.lieuNom}` : ''}{u.local ? ` · ${u.local}` : ''}
                    </span>
                  </li>
                ))}
                {scan.data.unites.length === 0 && (
                  <li className="text-sm text-ink-subtle">Aucune archive dans cette catégorie.</li>
                )}
              </ul>
            )}
          </Card>
        )}

        {onglet === 'inventaire' ? (
          <Card variant="glass" padded>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <Archive className="h-4 w-4 text-grc" aria-hidden="true" />
                Contenants
              </h2>
              <span className="flex flex-wrap items-center gap-2">
                {/* Étiquettes : des PLANCHES, pas une page par armoire — on en
                    colle trente d'un coup. */}
                {/* Téléchargements AUTHENTIFIÉS. Un `<a href>` renverrait 401 :
                    le jeton vit en mémoire, pas dans un cookie. */}
                <Button size="sm" variant="glass"
                  onClick={() => void api.admin.archives.etiquettes('tout')
                    .catch((e) => setError(e instanceof ApiException ? e.message : 'Téléchargement impossible'))}>
                  <QrCode className="h-4 w-4" aria-hidden="true" /> Étiquettes QR
                </Button>
                <Button size="sm" variant="glass"
                  onClick={() => void api.admin.archives.modeleImport()
                    .catch((e) => setError(e instanceof ApiException ? e.message : 'Téléchargement impossible'))}>
                  <Download className="h-4 w-4" aria-hidden="true" /> Modèle Excel
                </Button>
                <label>
                  <span className="inline-flex h-8 cursor-pointer items-center gap-1 rounded-r-md border border-border-strong/60 bg-surface/30 px-3 text-xs text-ink hover:bg-surface/50">
                    <Upload className="h-4 w-4" aria-hidden="true" /> Importer
                  </span>
                  <input type="file" className="hidden" accept=".xlsx,.xls"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void importer(f);
                      e.target.value = '';
                    }} />
                </label>
                <Button size="sm" onClick={() => setForm({
                  code: '', libelle: '', typeContenant: 'armoire',
                  lieuId: '', local: '', metrage: '', largeurSol: '', profondeurSol: '',
                })}>
                  <Plus className="h-4 w-4" aria-hidden="true" /> Nouveau contenant
                </Button>
              </span>
            </div>

            {importRapport && (
              <Alert variant={importRapport.erreurs.length > 0 ? 'warning' : 'success'}
                className="mb-4">
                <span>
                  {importRapport.unitesCreees} archives, {importRapport.contenantsCrees} contenants
                  et {importRapport.categoriesCreees} catégories créés.
                  {importRapport.ignorees > 0 && ` ${importRapport.ignorees} ligne(s) ignorée(s).`}
                  {importRapport.erreurs.length > 0 && (
                    <ul className="mt-1 list-disc pl-4 text-2xs">
                      {importRapport.erreurs.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  )}
                </span>
              </Alert>
            )}

            <p className="mb-4 flex items-start gap-1.5 text-xs text-ink-muted">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                Les archives se comptent en <strong>mètres linéaires</strong>, jamais
                en nombre de boîtes — une boîte n’a pas toujours la même épaisseur.
                Le cadre de classement proposé est celui de l’arrêté du 31 décembre
                1926 ({series.length} séries) ; vous pouvez le renommer et l’adapter.
              </span>
            </p>

            {form && (
              <div className="mb-4 grid gap-3 rounded-md border border-border bg-surface/40 p-3 sm:grid-cols-3">
                <Field label="Code" htmlFor="a-code" required hint="Imprimé sous le QR — ex. ARM-03">
                  <Input id="a-code" value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })} />
                </Field>
                <Field label="Libellé" htmlFor="a-lib" required>
                  <Input id="a-lib" value={form.libelle} placeholder="Armoire métallique, mur nord"
                    onChange={(e) => setForm({ ...form, libelle: e.target.value })} />
                </Field>
                <Field label="Type" htmlFor="a-type">
                  <select id="a-type" className={champ} value={form.typeContenant}
                    onChange={(e) => setForm({ ...form, typeContenant: e.target.value })}>
                    {TYPES_CONTENANT.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </Field>
                <Field label="Bâtiment" htmlFor="a-lieu">
                  <select id="a-lieu" className={champ} value={form.lieuId}
                    onChange={(e) => setForm({ ...form, lieuId: e.target.value })}>
                    <option value="">—</option>
                    {lieux.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
                  </select>
                </Field>
                <Field label="Local" htmlFor="a-local" hint="Grenier, sous-sol…">
                  <Input id="a-local" value={form.local}
                    onChange={(e) => setForm({ ...form, local: e.target.value })} />
                </Field>
                <Field label="Métrage linéaire (m)" htmlFor="a-metrage"
                  hint="Capacité : le cumul des tablettes">
                  <Input id="a-metrage" type="number" min={0} step="0.1" value={form.metrage}
                    onChange={(e) => setForm({ ...form, metrage: e.target.value })} />
                </Field>
                {/* Encombrement au sol — distinct de la capacité, et c'est lui
                    qui donne au meuble sa taille sur le plan. Facultatif : une
                    commune qui tient seulement son inventaire ne mesurera jamais
                    ses armoires. */}
                <Field label="Largeur au sol (m)" htmlFor="a-larg"
                  hint="Pour le plan à l’échelle — facultatif">
                  <Input id="a-larg" type="number" min={0.1} max={20} step="0.05"
                    value={form.largeurSol}
                    onChange={(e) => setForm({ ...form, largeurSol: e.target.value })} />
                </Field>
                <Field label="Profondeur au sol (m)" htmlFor="a-prof"
                  hint="Facultatif">
                  <Input id="a-prof" type="number" min={0.1} max={20} step="0.05"
                    value={form.profondeurSol}
                    onChange={(e) => setForm({ ...form, profondeurSol: e.target.value })} />
                </Field>
                <div className="flex gap-2 sm:col-span-3">
                  <Button onClick={() => void creerContenant()}>Créer</Button>
                  <Button variant="ghost" onClick={() => setForm(null)}>Annuler</Button>
                </div>
              </div>
            )}

            {loading ? (
              <p className="py-6 text-center text-sm text-ink-subtle">Chargement…</p>
            ) : contenants.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-subtle">
                Aucun contenant. Commencez par les armoires du local d’archives.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {contenants.map((c) => (
                  <li key={c.id} className="py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium tabular-nums text-ink">{c.code}</span>
                      <span className="text-sm text-ink">{c.libelle}</span>
                      {(c.lieuNom || c.local) && (
                        <span className="text-2xs text-ink-subtle">
                          {[c.lieuNom, c.local].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      <span className="ml-auto flex items-center gap-2 text-2xs">
                        {c.metrageLineaire !== null && (
                          <Badge
                            variant={c.metrageOccupe > c.metrageLineaire ? 'urgent' : 'neutral'}
                            size="sm"
                          >
                            {c.metrageOccupe.toFixed(1)} / {c.metrageLineaire.toFixed(1)} m
                          </Badge>
                        )}
                        {/* C'était un simple mot souligné : personne ne voyait
                            que c'était l'entrée pour saisir les boîtes (retour
                            du 31/07 — « c'est où qu'on intègre les unités ? »).
                            Un bouton explicite, et le mot « boîtes » plutôt que
                            « unités », qui est du vocabulaire d'archiviste. */}
                        <Button size="sm" variant="ghost"
                          onClick={() => void ouvrirUnites(c.id)}>
                          <Boxes className="h-4 w-4" aria-hidden="true" />
                          {c.nbUnites === 0
                            ? 'Ajouter des boîtes'
                            : `${c.nbUnites} boîte${c.nbUnites > 1 ? 's' : ''}`}
                        </Button>
                        {/* M4 (retour Manon) — la photo du contenant, imprimée
                            sur le procès-verbal de récolement. */}
                        {c.aPhoto && (
                          <Button size="sm" variant="ghost" title="Voir la photo du contenant"
                            onClick={() => void voirPhoto(c.id)}>
                            <Camera className="h-4 w-4" aria-hidden="true" /> Photo
                          </Button>
                        )}
                        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-muted hover:bg-surface"
                          title={c.aPhoto ? 'Remplacer la photo (imprimée sur le PV)' : 'Photographier le contenant (imprimée sur le PV)'}>
                          {!c.aPhoto && <Camera className="h-4 w-4" aria-hidden="true" />}
                          {c.aPhoto ? 'Remplacer' : 'Ajouter une photo'}
                          <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
                            className="sr-only" disabled={photoEnvoi === c.id}
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) void deposerPhoto(c.id, f); e.target.value = ''; }} />
                        </label>
                        <Button size="sm" variant="ghost" onClick={() => void supprimerContenant(c)}
                          aria-label={`Supprimer ${c.code}`}>
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </span>
                    </div>

                    {ouvert === c.id && (
                      <ul className="mt-2 space-y-0.5 border-l border-border pl-3">
                        {(unites[c.id] ?? []).length === 0 ? (
                          <li className="text-2xs text-ink-subtle">Contenant vide.</li>
                        ) : (unites[c.id] ?? []).map((u) => (
                          <li key={u.id} className="text-xs text-ink">
                            {u.serieLettre && (
                              <span className="font-medium">{u.serieLettre} </span>
                            )}
                            {u.cote && <span className="tabular-nums">{u.cote} · </span>}
                            {u.intitule}
                            {(u.anneeDebut || u.anneeFin) && (
                              <span className="text-ink-muted">
                                {' '}({u.anneeDebut ?? '?'}–{u.anneeFin ?? '?'})
                              </span>
                            )}
                            {/* M2 — le chemin de la version numérisée sur le
                                serveur de la mairie, tel qu'importé. */}
                            <FantomeDossier unite={u} onChange={() => void ouvrirUnites(c.id)} />
                            {u.cheminDemat && (
                              <span className="block truncate text-2xs text-ink-subtle" title={u.cheminDemat}>
                                💾 {u.cheminDemat}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : onglet === 'eliminations' ? (
          <ArchivesEliminationPanel />
        ) : onglet === 'plan' ? (
          <Card variant="glass" padded>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <Map className="h-4 w-4 text-grc" aria-hidden="true" />
                Plan du local
              </h2>
              <span className="flex items-center gap-2">
                <select aria-label="Local à afficher" className={champ + ' w-auto'}
                  value={localPlan}
                  onChange={(e) => { setLocalPlan(e.target.value); void chargerPlan(e.target.value); }}>
                  <option value="">— Choisir un local —</option>
                  {locaux.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.libelle}{l.lieuNom ? ` — ${l.lieuNom}` : ''}
                    </option>
                  ))}
                </select>
                <Button size="sm" variant="glass" onClick={() => void creerLocal()}>
                  <Plus className="h-4 w-4" aria-hidden="true" /> Local
                </Button>
              </span>
            </div>

            {planLocal ? (
              <>
                {/* « Calcul facilité » du retour mairie : le total du local. */}
                <p className="mb-3 text-xs text-ink-muted">
                  {planLocal.libelle} — <strong>{planLocal.totalMetrage.toFixed(1)} mètres
                  linéaires</strong> installés dans ce local.
                </p>
                <PlanSimple
                  elements={planElements}
                  largeur={planLocal.largeur}
                  hauteur={planLocal.hauteur}
                  largeurM={planLocal.largeurM}
                  hauteurM={planLocal.hauteurM}
                  traits={planTraits}
                  modifiable
                  onDeplacer={(id, x, y) => void deplacerContenant(id, x, y)}
                  onTracer={(t) => void (async () => {
                    try {
                      await api.admin.archives.ajouterTrait(localPlan, t);
                      await chargerPlan(localPlan);
                    } catch (e) {
                      setError(e instanceof ApiException ? e.message : 'Trait non enregistré');
                    }
                  })()}
                  onSupprimerTrait={(id) => void (async () => {
                    try {
                      await api.admin.archives.supprimerTrait(id);
                      await chargerPlan(localPlan);
                    } catch (e) {
                      setError(e instanceof ApiException ? e.message : 'Suppression impossible');
                    }
                  })()}
                  vide="Aucune armoire posée. Cliquez un code sous le plan pour la placer, puis faites-la glisser."
                />

                {/* L'échelle se saisit ici : sans elle, un carreau ne veut rien
                    dire et le « calcul facilité » du retour mairie tombe à plat. */}
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <Field label="Largeur du local (m)" htmlFor="pl-l">
                    <Input id="pl-l" type="number" min={0.5} step="0.1"
                      defaultValue={planLocal.largeurM ?? ''}
                      onBlur={(e) => void (async () => {
                        const v = e.target.value ? Number(e.target.value) : null;
                        await api.admin.archives.majDimensionsLocal(localPlan, {
                          largeurM: v, hauteurM: planLocal.hauteurM,
                        }).then(() => chargerPlan(localPlan)).catch(() => {});
                      })()} />
                  </Field>
                  <Field label="Profondeur (m)" htmlFor="pl-h">
                    <Input id="pl-h" type="number" min={0.5} step="0.1"
                      defaultValue={planLocal.hauteurM ?? ''}
                      onBlur={(e) => void (async () => {
                        const v = e.target.value ? Number(e.target.value) : null;
                        await api.admin.archives.majDimensionsLocal(localPlan, {
                          largeurM: planLocal.largeurM, hauteurM: v,
                        }).then(() => chargerPlan(localPlan)).catch(() => {});
                      })()} />
                  </Field>
                </div>
              </>
            ) : (
              <p className="text-sm text-ink-subtle">
                Choisissez un local, ou créez-en un. Une mairie archive souvent à
                plusieurs endroits : chacun a son plan.
              </p>
            )}
          </Card>
        ) : onglet === 'categories' ? (
          <Card variant="glass" padded>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <Tags className="h-4 w-4 text-grc" aria-hidden="true" />
                Vos catégories
              </h2>
              <Button size="sm" onClick={() => void creerCategorie()}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Nouvelle catégorie
              </Button>
            </div>

            <p className="mb-4 flex items-start gap-1.5 text-xs text-ink-muted">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                C’est <strong>votre</strong> nomenclature — le « type de données »
                que vous utilisez déjà. Rien n’est pré-rempli : la mairie décide.
                Chaque catégorie porte son propre QR : le scanner liste ses
                archives <strong>où qu’elles soient rangées</strong>.
                {series.length > 0 && (
                  <> Le cadre de classement réglementaire de 1926 reste disponible,
                  désactivé, pour les communes qui déposent aux Archives
                  départementales.</>
                )}
              </span>
            </p>

            {categories.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-subtle">
                Aucune catégorie. Créez-les au fur et à mesure, ou laissez l’import
                Excel les créer toutes seules à partir de votre fichier.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {categories.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                    <span className="text-ink">{c.libelle}</span>
                    <span className="text-2xs text-ink-subtle">code {c.code}</span>
                    <span className="ml-auto flex items-center gap-2 text-2xs">
                      {/* M3 — la DUA et le sort final de ce type de document. */}
                      <CategorieDuaEditor categorie={c} onSaved={() => void charger()} />
                      <Badge variant="neutral" size="sm">
                        {c.nbUnites} archive{c.nbUnites > 1 ? 's' : ''} · {c.metrage.toFixed(1)} m
                      </Badge>
                      <Button size="sm" variant="ghost" onClick={() => void supprimerCategorie(c)}
                        aria-label={`Supprimer ${c.libelle}`}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : (
          <Card variant="glass" padded>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <ClipboardCheck className="h-4 w-4 text-grc" aria-hidden="true" />
                Campagnes de récolement
              </h2>
              <Button size="sm" onClick={() => void ouvrirCampagne()}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Ouvrir une campagne
              </Button>
            </div>

            <p className="mb-4 flex items-start gap-1.5 text-xs text-ink-muted">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                Le procès-verbal de récolement est obligatoire à chaque changement de
                maire (code du patrimoine, art. L212-11). Une campagne ne peut être
                close <strong>que si tous les contenants ont été vus</strong> : un
                procès-verbal ne peut pas attester de ce qui n’a pas été vérifié.
              </span>
            </p>

            <ul className="mb-4 divide-y divide-border">
              {recolements.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <button type="button" className="text-ink underline"
                    onClick={() => { setCampagne(r.id); void chargerEtat(r.id); }}>
                    {r.libelle}
                  </button>
                  <span className="text-2xs text-ink-subtle">
                    ouverte le {fr(r.debutLe)}
                  </span>
                  <span className="ml-auto flex items-center gap-2">
                    <Badge variant="neutral" size="sm">
                      {r.passages} / {r.totalContenants} vus
                    </Badge>
                    {r.ecarts > 0 && (
                      <Badge variant="warning" size="sm">{r.ecarts} écart{r.ecarts > 1 ? 's' : ''}</Badge>
                    )}
                    {r.closLe
                      ? <Badge variant="success" size="sm">Close le {fr(r.closLe)}</Badge>
                      : <Badge variant="warning" size="sm">En cours</Badge>}
                  </span>
                </li>
              ))}
              {recolements.length === 0 && (
                <li className="py-4 text-center text-sm text-ink-subtle">
                  Aucune campagne. Ouvrez-en une pour commencer le tour des armoires.
                </li>
              )}
            </ul>

            {campagne && campagneCourante && (
              <div className="rounded-md border border-border bg-surface/40 p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-ink">{campagneCourante.libelle}</span>
                  <span className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="glass"
                      onClick={() => void api.admin.archives.exportRecolementPdf(campagne)
                        .catch((e) => setError(e instanceof ApiException ? e.message : 'Export impossible'))}>
                      <Download className="h-4 w-4" aria-hidden="true" /> PV (PDF)
                    </Button>
                    <Button size="sm" variant="glass"
                      onClick={() => void api.admin.archives.exportRecolementXlsx(campagne)
                        .catch((e) => setError(e instanceof ApiException ? e.message : 'Export impossible'))}>
                      <Download className="h-4 w-4" aria-hidden="true" /> Excel
                    </Button>
                    {!campagneCourante.closLe && (
                      <Button size="sm" variant="glass" onClick={() => void clore()}>
                        Clore la campagne
                      </Button>
                    )}
                  </span>
                </div>

                <ul className="divide-y divide-border">
                  {etat.map((l) => (
                    <li key={l.contenantId} className="py-2">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="tabular-nums text-ink">{l.code}</span>
                        <span className="text-ink-muted">{l.libelle}</span>
                        <span className="ml-auto flex items-center gap-2">
                          {l.constat === null && (
                            <Badge variant="neutral" size="sm">Pas encore vu</Badge>
                          )}
                          {l.constat === 'conforme' && (
                            <Badge variant="success" size="sm">Conforme · {fr(l.faitLe)}</Badge>
                          )}
                          {l.constat === 'ecart' && (
                            <Badge variant="warning" size="sm">Écart · {fr(l.faitLe)}</Badge>
                          )}
                          {l.constat === 'introuvable' && (
                            <Badge variant="urgent" size="sm">Introuvable</Badge>
                          )}
                          {!campagneCourante.closLe && (
                            <Button size="sm" variant="ghost"
                              onClick={() => {
                                setConstatDe(constatDe === l.contenantId ? null : l.contenantId);
                                setObservation(l.observations ?? '');
                              }}>
                              {l.constat === null ? 'Constater' : 'Corriger'}
                            </Button>
                          )}
                        </span>
                      </div>

                      {l.observations && constatDe !== l.contenantId && (
                        <p className="mt-1 text-2xs text-ink-subtle">{l.observations}</p>
                      )}

                      {constatDe === l.contenantId && (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-start gap-2">
                            <textarea
                              rows={2} value={observation}
                              onChange={(e) => setObservation(e.target.value)}
                              placeholder="Observations — dictez plutôt que de taper, vous êtes debout dans un grenier"
                              className="w-full rounded-md border border-border bg-surface/60 p-2 text-sm text-ink"
                            />
                            {/* La dictée : c'est le geste du terrain. Le hook
                                existe déjà (devis vocal), on le réutilise. */}
                            {dictee.supporte && (
                              <Button size="sm" variant={dictee.ecoute ? 'danger' : 'glass'}
                                onClick={() => (dictee.ecoute ? dictee.arreter() : dictee.demarrer())}
                                aria-label={dictee.ecoute ? 'Arrêter la dictée' : 'Dicter'}>
                                {dictee.ecoute
                                  ? <MicOff className="h-4 w-4" aria-hidden="true" />
                                  : <Mic className="h-4 w-4" aria-hidden="true" />}
                              </Button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="success"
                              onClick={() => void constater(l.contenantId, 'conforme')}>
                              <Check className="h-4 w-4" aria-hidden="true" /> Conforme
                            </Button>
                            <Button size="sm" variant="glass"
                              onClick={() => void constater(l.contenantId, 'ecart')}>
                              <HelpCircle className="h-4 w-4" aria-hidden="true" /> Écart
                            </Button>
                            <Button size="sm" variant="danger"
                              onClick={() => void constater(l.contenantId, 'introuvable')}>
                              <X className="h-4 w-4" aria-hidden="true" /> Introuvable
                            </Button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}
